/**
 * The app-wide side of the right-click menu (the menu itself is `state/menu.ts`):
 *
 * - the browser's own menu never shows: a right click that no window handled opens the
 *   general menu, or cut/copy/paste in a text field;
 * - in release builds the developer-tools shortcuts are blocked (F12, Ctrl+Shift+I/J/C,
 *   Ctrl+U). Inside the desktop app this, with Tauri's release build having no developer
 *   tools, leaves no way to inspect the page. In a browser it only discourages: the browser's
 *   own menus can always open its tools, and a web page's code is always readable.
 */
import { opensDevTools } from "./keys";
import type { App } from "./context";
import { isOpen } from "../wm/layout";
import { SEPARATOR, type MenuEntry } from "../state/menu";
import { toast } from "../shared/toast";

/** The general entries: undo/redo, save, windows, plugin commands, theme, layout. */
export function generalEntries(app: App): MenuEntry[] {
  const { graph, wm, theme, plugins } = app;
  const pluginCommands = plugins.commands();
  return [
    { label: "Undo", command: "graph.undo", disabled: !graph.state.canUndo },
    { label: "Redo", command: "graph.redo", disabled: !graph.state.canRedo },
    SEPARATOR,
    { label: "Save project", command: "project.save" },
    SEPARATOR,
    {
      label: "Windows",
      items: [
        ...wm.windows().map((w) => ({ label: w.title, checked: isOpen(wm.layout(), w.id), action: () => wm.toggle(w.id) })),
        SEPARATOR,
        { label: "Reset the layout", action: () => wm.reset() },
      ],
    },
    ...(pluginCommands.length ? [{ label: "Plugins", items: pluginCommands.map((c) => ({ label: c.label, command: c.name })) }] : []),
    {
      label: "Theme",
      items: (["system", "light", "dark"] as const).map((mode) => ({
        label: mode[0]!.toUpperCase() + mode.slice(1),
        checked: theme.mode() === mode,
        action: () => theme.set(mode),
      })),
    },
  ];
}

/** `true` for a text field (or any editable element). */
function isEditable(element: EventTarget | null): element is HTMLElement {
  return element instanceof HTMLElement && (element.matches("input:not([type=checkbox]):not([type=radio]):not([type=range]), textarea") || element.isContentEditable);
}

/** Cut, copy, paste and select all, for the text field `field`. */
export function textEntries(field: HTMLElement): MenuEntry[] {
  const input = field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field : null;
  const hasSelection = input ? input.selectionStart !== input.selectionEnd : !!getSelection()?.toString();
  const readOnly = input ? input.readOnly || input.disabled : !field.isContentEditable;
  /** Runs an editing command on the field. */
  const exec = (command: string) => {
    field.focus();
    document.execCommand(command);
  };
  return [
    { label: "Cut", disabled: !hasSelection || readOnly, action: () => exec("cut") },
    { label: "Copy", disabled: !hasSelection, action: () => exec("copy") },
    {
      label: "Paste",
      disabled: readOnly,
      action: () => {
        field.focus();
        navigator.clipboard
          ?.readText()
          .then((text) => document.execCommand("insertText", false, text))
          .catch(() => toast("Paste with Ctrl+V: this window may not read the clipboard."));
      },
    },
    SEPARATOR,
    { label: "Select all", action: () => (input ? input.select() : exec("selectAll")) },
  ];
}

/**
 * Installs the right-click handling and, when `blockDevTools`, the shortcut blocking.
 * Returns a function that removes both.
 */
export function installContextMenu(app: App, blockDevTools: boolean): () => void {
  app.menu.setGeneral(() => generalEntries(app));
  /** Runs after the windows' own handlers (bubbling): fills in when none of them did. */
  const onMenu = (e: MouseEvent) => {
    e.preventDefault();
    if (app.menu.handled(e)) return;
    app.menu.open(e, isEditable(e.target) ? textEntries(e.target) : generalEntries(app));
  };
  /** Swallows the developer-tools shortcuts before anything else sees them. */
  const onKey = (e: KeyboardEvent) => {
    if (!opensDevTools(e)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  addEventListener("contextmenu", onMenu);
  if (blockDevTools) addEventListener("keydown", onKey, true);
  return () => {
    removeEventListener("contextmenu", onMenu);
    removeEventListener("keydown", onKey, true);
  };
}
